import { useEffect, useRef, useState } from "react";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { ArrowLeft, Plus } from "lucide-react";
import { Button } from "~/atoms/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "~/atoms/tabs";
import { EventDialog, type EventDraft } from "./EventDialog";
import { EventList } from "./EventList";
import {
  eventsInDomain,
  formatDate,
  formatReviewPeriod,
  horizonYear,
  shiftYears,
  yearDomain,
  type DocumentReview,
  type ReviewWindow,
} from "./helpers";
import { useText } from "./text";
import { historyHorizon } from "./timeline/timelineHelpers";
import { ReviewWindowsLine } from "./timeline/ReviewWindowsLine";
import { TimelineControls } from "./timeline/TimelineControls";
import {
  annualWheelTabs,
  type AnnualWheelEntry,
  type AnnualWheelEvent,
  type AnnualWheelTab,
} from "./types";
import { useTween } from "./useTween";
import { reviewWindowsInYear } from "./wheel/wheelHelpers";
import { ReviewWindowsWheel } from "./wheel/ReviewWindowsWheel";
import { WorkflowStepLegend } from "./WorkflowStepLegend";

interface Props {
  today: Date;
  // The review of every release, previous and upcoming ones included
  documentReviews: DocumentReview[];
  reviewWindows: ReviewWindow[];
  events: AnnualWheelEvent[];
  // Set when the reviews are those of one document, which the timeline then follows
  selectedEntry?: AnnualWheelEntry;
  tab: AnnualWheelTab;
  onTabChange: (tab: string) => void;
}

// The only place that names both views: dropping the wheel means dropping its tab and its branch here
export const ReviewWindowsCharts = ({
  today,
  documentReviews,
  reviewWindows,
  events,
  selectedEntry,
  tab,
  onTabChange,
}: Props) => {
  const { t } = useTranslation();
  const text = useText();
  const history = selectedEntry && historyHorizon(documentReviews);
  const isDocumentHistory = tab === "line" && selectedEntry !== undefined;
  // Both views share one place in time; the timeline can move it freely
  const [horizon, setHorizon] = useState(
    () =>
      (isDocumentHistory ? history : undefined) ??
      yearDomain(today.getFullYear()),
  );
  const year = horizonYear(horizon);
  const [followedEntryId, setFollowedEntryId] = useState(selectedEntry?.id);
  // The timeline starts following a document with the whole of its history in view
  if (selectedEntry?.id !== followedEntryId) {
    setFollowedEntryId(selectedEntry?.id);
    if (isDocumentHistory && history) setHorizon(history);
  }
  const [openedReviewFrom, setOpenedReviewFrom] = useState<number>();
  const [isOpen, setIsOpen] = useState(false);
  const openedWindow = reviewWindows.find(
    (reviewWindow) => reviewWindow.reviewFrom.getTime() === openedReviewFrom,
  );
  const isZoomedIn = isOpen && openedWindow !== undefined;
  const tween = useTween(isZoomedIn ? 1 : 0);
  const backButtonRef = useRef<HTMLButtonElement>(null);
  const [eventDraft, setEventDraft] = useState<EventDraft>();
  const [highlightedEventId, setHighlightedEventId] = useState<number>();

  useEffect(() => {
    if (!isZoomedIn) return;

    // The mark that was clicked is gone; autoFocus would also scroll up here
    backButtonRef.current?.focus({ preventScroll: true });

    // Radix prevents the default when Escape closes one of its own layers
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !event.defaultPrevented) setIsOpen(false);
    };

    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [isZoomedIn]);

  const chartProps = {
    today,
    openedWindow,
    zoom: openedWindow ? tween : 0,
    onOpen: (reviewWindow: ReviewWindow) => {
      setOpenedReviewFrom(reviewWindow.reviewFrom.getTime());
      setIsOpen(true);
    },
    events,
    highlightedEventId,
    onEventOpen: setEventDraft,
  };
  // A new event starts on today's day of the year that is in view
  const addEvent = () =>
    setEventDraft({
      name: "",
      date: formatDate(dayjs(today).year(year).toDate(), "isoDate"),
      color: "blue",
    });
  // The timeline keeps its zoom and its place in the year when the year changes
  const changeYear = (nextYear: number) =>
    setHorizon(shiftYears(horizon, nextYear - year));
  const description = () => {
    if (isZoomedIn) return text.documentsDescription[tab];
    if (isDocumentHistory) return text.documentHistoryDescription;

    return text.yearDescription[tab];
  };
  // Remounting on every zoom in and out drops the tooltip of the view left behind
  const chartKey = isZoomedIn ? openedReviewFrom : "year";

  return (
    <Tabs value={tab} onValueChange={onTabChange}>
      <div className="relative">
        <TabsList layout="compact">
          {annualWheelTabs.map((value) => (
            <TabsTrigger key={value} value={value}>
              {text.view[value]}
            </TabsTrigger>
          ))}
        </TabsList>
        <Button
          variant="outline"
          size="sm"
          // A narrow screen has no room beside the tabs; the events list has it there
          className="absolute top-1/2 right-0 -translate-y-1/2 max-sm:hidden"
          onClick={addEvent}
        >
          <Plus />
          {text.events.add}
        </Button>
      </div>

      {/* One panel for both views, so the zoom and its Back button survive a switch */}
      <TabsContent value={tab} className="flex flex-col gap-4">
        <div className="flex flex-col gap-1">
          <p className="text-muted-foreground">{description()}</p>
          <div className="flex min-h-9 flex-wrap items-center justify-between gap-x-6 gap-y-2">
            {isZoomedIn ? (
              <>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                  <Button
                    variant="outline"
                    size="sm"
                    ref={backButtonRef}
                    onClick={() => setIsOpen(false)}
                  >
                    <ArrowLeft />
                    {tab === "wheel"
                      ? text.backToYear(year)
                      : t(($) => $.commons.buttons.back)}
                  </Button>
                  <span className="font-semibold whitespace-nowrap">
                    {formatReviewPeriod(openedWindow)}
                  </span>
                  <span className="text-muted-foreground whitespace-nowrap">
                    {text.documents(openedWindow.documentReviews.length)}
                  </span>
                </div>
                <WorkflowStepLegend
                  documentReviews={openedWindow.documentReviews}
                />
              </>
            ) : (
              <>
                <p className="text-muted-foreground">
                  {!isDocumentHistory && text.openHint}
                  {tab === "line" && ` ${text.timelineHint}`}
                </p>
                {tab === "line" && (
                  <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
                    {isDocumentHistory && (
                      <WorkflowStepLegend documentReviews={documentReviews} />
                    )}
                    <TimelineControls
                      horizon={horizon}
                      fitLabel={history ? text.wholeHistory : text.wholeYear}
                      fitHorizon={history ?? yearDomain(year)}
                      onHorizonChange={setHorizon}
                    />
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {tab === "wheel" ? (
          <ReviewWindowsWheel
            key={chartKey}
            {...chartProps}
            year={year}
            reviewWindows={reviewWindowsInYear(reviewWindows, year)}
            onClose={() => setIsOpen(false)}
            onYearChange={changeYear}
          />
        ) : (
          <ReviewWindowsLine
            key={chartKey}
            {...chartProps}
            reviewWindows={reviewWindows}
            horizon={horizon}
            onHorizonChange={setHorizon}
            isDocumentHistory={isDocumentHistory}
          />
        )}

        <EventList
          year={year}
          events={eventsInDomain(events, yearDomain(year))}
          onAdd={addEvent}
          onOpen={setEventDraft}
          onHighlight={setHighlightedEventId}
        />
      </TabsContent>

      {eventDraft && (
        <EventDialog
          key={eventDraft.id ?? "new"}
          draft={eventDraft}
          onClose={() => setEventDraft(undefined)}
        />
      )}
    </Tabs>
  );
};
